import { Line } from 'react-chartjs-2';
import 'chart.js/auto';
import { useState } from 'react';
import { clsx } from 'clsx';
import { useTranslate } from '@tolgee/react';
import { useChartTheme } from '~/helper/chartTheme';

interface Props {
  readonly datasetIdKey: string;
  readonly priceHubbleHistory: {
    readonly value: number;
    readonly lowerValue: number;
    readonly upperValue: number;
    readonly date: number;
  }[];
}

export function PriceDevelopmentHistoryChart(props: Props) {
  const { t } = useTranslate();
  const theme = useChartTheme();
  const [
    timeFilter,
    setTimeFilter,
  ] = useState(10);

  const getTabs = () => [
    {
      name: `2 ${t('price_development_history_chart_years')}`,
      href: '#',
      current: timeFilter === 2,
    },
    {
      name: `5 ${t('price_development_history_chart_years')}`,
      href: '#',
      current: timeFilter === 5,
    },
    {
      name: `10 ${t('price_development_history_chart_years')}`,
      href: '#',
      current: timeFilter === 10,
    },
  ];

  const [
    tabs,
    setTabs,
  ] = useState(getTabs());

  const handleSelectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const years = parseInt(event.target.value.split(' ')[0], 10);
    setTimeFilter(years);
    setTabs(getTabs());
  };

  const filterDataByTime = (years: number) => {
    const currentDate = new Date();
    return props.priceHubbleHistory.filter((data) => {
      const dataDate = new Date(data.date);
      return currentDate.getFullYear() - dataDate.getFullYear() <= years;
    });
  };

  const filteredPriceDevelopment = filterDataByTime(timeFilter);

  // Filter labels and data for every second (or n-th) position
  const filteredLabels = filteredPriceDevelopment
    .map((e, index) =>
      index % 2 === 0 || timeFilter !== 10
        ? new Date(e.date).getFullYear().toString()
        : '',
    )
    .filter((label) => label !== '');
  const filteredData = filteredPriceDevelopment
    .filter((_, index) => index % 2 === 0 || timeFilter !== 10)
    .map((e) => e.value);

  const chartData = {
    labels: filteredLabels,
    datasets: [
      {
        label: t('evaluation_history_chart_estimated_value_label'),
        data: filteredData,
        // Token --color-chart-1 -> primary-500; Whitelabel (WhitelabelStyle) schaltet um.
        borderColor: theme.series[0],
        fill: false,
      },
    ],
  };

  return (
    <div className="dark:text-gray-300">
      <div>
        <div className="sm:hidden p-3">
          <label
            htmlFor="tabs"
            className="sr-only"
          >
            Select a tab
          </label>
          <select
            id="tabs"
            name="tabs"
            className="block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 focus-visible:border-primary-500 focus-visible:ring-primary-500"
            onChange={handleSelectChange}
            defaultValue={tabs.find((tab) => tab.current)?.name}
          >
            {tabs.map((tab, idx) => (
              <option key={idx}>{tab.name}</option>
            ))}
          </select>
        </div>
        <div className="hidden sm:block">
          <nav
            className="isolate flex divide-x divide-gray-200 dark:divide-gray-700 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700"
            aria-label="Tabs"
          >
            {tabs.map((tab, tabIdx) => (
              <a
                key={tabIdx}
                href={tab.href}
                onClick={(e) => {
                  e.preventDefault();
                  setTimeFilter(
                    tab.name === '2 Jahre'
                      ? 2
                      : tab.name === '5 Jahre'
                      ? 5
                      : 10,
                  );
                  setTabs(
                    tabs.map((t, tIdx) => {
                      return {
                        ...t,
                        current: tabIdx === tIdx,
                      };
                    }),
                  );
                }}
                className={clsx(
                  tab.current ? 'bg-primary-200' : 'bg-transparent',
                  tab.current
                    ? 'text-gray-900 dark:text-gray-100'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 dark:text-gray-300 dark:hover:text-gray-300',
                  'group relative min-w-0 flex-1 overflow-hidden py-4 px-4 text-center text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-800 dark:hover:bg-gray-700 focus-visible:z-10',
                )}
                aria-current={tab.current ? 'page' : undefined}
              >
                <span className="uppercase">{tab.name}</span>
              </a>
            ))}
          </nav>
        </div>
      </div>
      <div className="w-full p-3 md:p-6 aspect-4/3">
        <Line
          datasetIdKey={props.datasetIdKey}
          data={chartData as any}
          options={
            {
              responsive: true,
              maintainAspectRatio: false,
              layout: {
                padding: {
                  right: 30,
                },
              },
              plugins: {
                legend: { display: false },
                tooltip: {
                  callbacks: {
                    label: function (context: {
                      dataset: { label: string; data: number[] };
                      dataIndex: number;
                    }) {
                      return (
                        context.dataset.label +
                        ': ' +
                        context.dataset?.data?.[
                          context?.dataIndex
                        ]?.toLocaleString('de-CH', {
                          style: 'currency',
                          currency: 'CHF',
                          maximumFractionDigits: 0,
                        })
                      );
                    },
                  },
                  mode: 'index',
                  intersect: false,
                },
                datalabels: {
                  display: false,
                },
              },
              scales: {
                y: {
                  beginAtZero: false,
                  grid: { color: theme.grid },
                  ticks: {
                    color: theme.tick,
                    callback: function (value: string | number | Date) {
                      return value.toLocaleString('de-CH', {
                        style: 'currency',
                        currency: 'CHF',
                        maximumFractionDigits: 0,
                      });
                    },
                    padding: 10,
                    font: {
                      size: 16,
                    },
                  },
                },
                x: {
                  grid: { color: theme.grid },
                  ticks: {
                    color: theme.tick,
                    padding: 10,
                    font: {
                      size: 16,
                    },
                    autoSkip: true,
                    maxTicksLimit: 20,
                  },
                },
              },
            } as any
          }
        />
      </div>
    </div>
  );
}
